import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Header from '../components/Header';
import LogService, { LOG_ACTIONS } from '../services/LogService';

const ACTIONS_LIST = Object.values(LOG_ACTIONS);
const PAGE_SIZE = 50;

const ACTION_LABELS = {
  login_success: 'Login OK',
  login_fail: 'Login Falha',
  logout: 'Logout',
  register: 'Registro',
  upload: 'Upload',
  analysis_start: 'Análise Início',
  analysis_end: 'Análise Fim',
  analysis_error: 'Análise Erro',
  download_excel: 'Download Excel',
  download_pdf: 'Download PDF',
  view_plans: 'Ver Planos',
  payment_attempt: 'Pagamento',
  feedback_positive: 'Feedback +',
  feedback_negative: 'Feedback -',
  manual_item_add: 'Item Manual',
  consent: 'Consentimento',
  cub_calculate: 'Calculo CUB',
  cub_admin_update: 'CUB Atualizado',
  sinapi_search: 'SINAPI Busca',
  sinapi_view_composicao: 'SINAPI Detalhe',
  sinapi_add_to_orcamento: 'SINAPI + Orç',
  sinapi_remove_from_orcamento: 'SINAPI - Orç',
  sinapi_calculate: 'SINAPI Cálculo',
  sinapi_export_excel: 'SINAPI Excel',
  sinapi_export_pdf: 'SINAPI PDF',
  sinapi_import_start: 'SINAPI Import',
  sinapi_import_complete: 'SINAPI OK',
  sinapi_import_error: 'SINAPI Erro',
};

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR');
}

function AdminLogs() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    userEmail: '',
    action: '',
    status: '',
  });
  const [page, setPage] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const filteredLogs = useMemo(() => {
    return LogService.getFiltered(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, refreshKey]);

  const paginatedLogs = filteredLogs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filteredLogs.length / PAGE_SIZE);

  const handleExportExcel = () => {
    const wsData = [
      ['ID', 'Data/Hora', 'User ID', 'Email', 'Ação', 'Status', 'Detalhes', 'Erro', 'IP', 'User Agent'],
      ...filteredLogs.map((log) => [
        log.id,
        formatDate(log.timestamp),
        log.user_id,
        log.user_email,
        ACTION_LABELS[log.action] || log.action,
        log.status,
        JSON.stringify(log.details),
        log.error_message || '',
        log.ip_address,
        log.user_agent,
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [
      { wch: 25 }, { wch: 20 }, { wch: 15 }, { wch: 25 },
      { wch: 18 }, { wch: 10 }, { wch: 40 }, { wch: 30 },
      { wch: 15 }, { wch: 40 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Logs');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `QuantoCustaObra_Logs_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleClearLogs = () => {
    if (window.confirm('Tem certeza que deseja limpar todos os logs? Esta ação não pode ser desfeita.')) {
      LogService.clearAll();
      setRefreshKey((k) => k + 1);
    }
  };

  const statusColor = (status) => {
    if (status === 'success') return 'bg-green-100 text-green-700';
    if (status === 'error') return 'bg-red-100 text-red-700';
    return 'bg-blue-100 text-blue-700';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Painel de Logs</h1>
            <p className="text-gray-500">{filteredLogs.length} registros encontrados</p>
          </div>
          <div className="flex gap-2 mt-3 sm:mt-0">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              Dashboard
            </button>
            <button
              onClick={() => navigate('/admin/feedbacks')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              Feedbacks
            </button>
            <button
              onClick={() => navigate('/admin/usuarios')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              Usuários
            </button>
            <button
              onClick={() => navigate('/admin/cub')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              CUB
            </button>
            <button
              onClick={() => navigate('/admin/sinapi')}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              SINAPI
            </button>
            <button
              onClick={() => setRefreshKey((k) => k + 1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              Atualizar
            </button>
            <button
              onClick={handleExportExcel}
              disabled={filteredLogs.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Exportar Excel
            </button>
            <button
              onClick={handleClearLogs}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 cursor-pointer transition-colors"
            >
              Limpar Logs
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Data início</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => updateFilter('startDate', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Data fim</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => updateFilter('endDate', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
              <input
                type="text"
                value={filters.userEmail}
                onChange={(e) => updateFilter('userEmail', e.target.value)}
                placeholder="Buscar por email..."
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Ação</label>
              <select
                value={filters.action}
                onChange={(e) => updateFilter('action', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
              >
                <option value="">Todas</option>
                {ACTIONS_LIST.map((a) => (
                  <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => updateFilter('status', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
              >
                <option value="">Todos</option>
                <option value="success">Success</option>
                <option value="error">Error</option>
                <option value="info">Info</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left">
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Data/Hora</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Email</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Ação</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Detalhes</th>
                  <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Erro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                      Nenhum log encontrado
                    </td>
                  </tr>
                ) : (
                  paginatedLogs.map((log) => (
                    <tr
                      key={log.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                    >
                      <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(log.timestamp)}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-[180px] truncate">{log.user_email}</td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-orca-100 text-orca-700">
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(log.status)}`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 max-w-[200px] truncate text-xs">
                        {Object.keys(log.details).length > 0 ? JSON.stringify(log.details) : '-'}
                      </td>
                      <td className="px-4 py-3 text-red-600 max-w-[150px] truncate text-xs">
                        {log.error_message || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Expanded row detail */}
          {expandedRow && (() => {
            const log = paginatedLogs.find((l) => l.id === expandedRow);
            if (!log) return null;
            return (
              <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div><span className="font-medium text-gray-600">ID:</span> <span className="text-gray-800">{log.id}</span></div>
                  <div><span className="font-medium text-gray-600">User ID:</span> <span className="text-gray-800">{log.user_id}</span></div>
                  <div><span className="font-medium text-gray-600">IP:</span> <span className="text-gray-800">{log.ip_address}</span></div>
                  <div className="md:col-span-2"><span className="font-medium text-gray-600">User Agent:</span> <span className="text-gray-800 break-all text-xs">{log.user_agent}</span></div>
                  <div className="md:col-span-2">
                    <span className="font-medium text-gray-600">Detalhes completos:</span>
                    <pre className="mt-1 p-3 bg-white rounded-lg border border-gray-200 text-xs text-gray-700 overflow-x-auto">
                      {JSON.stringify(log.details, null, 2)}
                    </pre>
                  </div>
                  {log.error_message && (
                    <div className="md:col-span-2">
                      <span className="font-medium text-red-600">Erro:</span>
                      <p className="mt-1 text-red-700 text-sm">{log.error_message}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Anterior
            </button>
            <span className="text-sm text-gray-500">
              {page + 1} de {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Próximo
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminLogs;
